import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrShareDataQuery } from './api.graphql';
import { calculateNextRoundGoal } from './calculateNextRoundGoal';
import { getDisplayablePetitionPhoto } from './getDisplayablePetitionPhoto';
import { normalizeJoinTheCommunityCustomConfig } from './normalize/normalizeJoinTheCommunityCustomConfig';
import { normalizePromoteShareOnComboExperiment } from './normalize/normalizePromoteShareOnComboExperiment';
import { PSF_SHARE_FIRST_SOCIAL_STARTER_COMBO_FALLBACK } from './normalize/socialStarterControlExperiments';
import type {
	FcmValues,
	Petition,
	PromoteShareOnComboExperiment,
	PromotionStats,
	PsfPromoteOrSharePageData,
	Recruiter,
	Viewer,
} from './types';

type Experiments = PsfPromoteOrShareDataQuery['experiments'];

const CHIP_IN_USD_FALLBACK = 3;
const CHIP_IN_USD_SHARE_EXPERIMENT_AMOUNT = 5;

type PsfComboBanditData = {
	amount: number;
	currency: string;
	amountLocalized: string;
};

/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized. This was actually copied from the PSF Share page page data context.
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

function normalizeExperimentFlags(experiments: Experiments) {
	return {
		whatsappHeadlineExperiment: {
			variation: experiments.psfWhatsappShareCopyHeadline.variation,
			name: experiments.psfWhatsappShareCopyHeadline.experimentName,
			variants: experiments.psfWhatsappShareCopyHeadline.config,
		},
		advocateEntryPointExperiment: {
			variation: experiments.advocateEntryPoint.variation,
			name: experiments.advocateEntryPoint.experimentName,
		},
		// Passed through unnarrowed: usePageCopy needs variation + config (per-variant copy payload).
		psfComboCopyExperiment: experiments.psfComboCopyExperiment,
		// Full resolved object, unnarrowed for the same reason: treat:false in the query; treatExperiment
		// fires at the Facebook tap in useTreatFacebookPostSheetExperiment (CHANGE-99697).
		psfPromoteOrShareFacebookPostSheetExperiment: experiments.psfPromoteOrShareFacebookPostSheetExperiment,
		// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
		nativeShareTitleExperiment: experiments.nativeShareTitleExperiment,
		// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
		// Full resolved object, same reason: treat:false in the query; treatExperiment fires on slide-up
		// open in useTreatQrCodeExperiment (CHANGE-99266).
		psfPromoteOrShareQrCodeExperiment: experiments.psfPromoteOrShareQrCodeExperiment,
		// EXPERIMENT: psf_og_text_experiment — remove with experiment cleanup
		psfOgTextExperiment: experiments.psfOgTextExperiment,
		// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
		// Full resolved object, same reason: treat:false in the query; treatExperiment fires on slide-up
		// open in useEnrollConstituentPsfExperiment (CHANGE-100768).
		constituentPsfExperiment: experiments.constituentPsfExperiment,
	};
}

function psfComboBanditVariationWhenEnabled(
	cookies: UtilityContext['cookies'],
	banditComboExpConfig: NonNullable<PsfPromoteOrShareDataQuery['featureConfigs']['banditComboExpConfig']>,
	psfComboExpPercentage: number,
): 'static' | 'default' {
	const PSF_COMBO_BANDIT_R7 = 'psf_combo_bandit_r7';
	if (
		cookies.get(PSF_COMBO_BANDIT_R7) === 'psf_combo_bandit_r7_static' ||
		(cookies.get(PSF_COMBO_BANDIT_R7) === undefined &&
			psfComboExpPercentage <= banditComboExpConfig.treatment_traffic_percentage)
	) {
		return 'static';
	}
	return 'default';
}

function resolvePsfComboBandit(
	utilityContext: UtilityContext,
	featureConfigs: PsfPromoteOrShareDataQuery['featureConfigs'],
	viewer: Viewer,
): { psfComboBanditVariation: 'static' | 'default' | null; psfComboData: PsfComboBanditData | null } {
	const { banditComboExpConfig } = featureConfigs;
	if (!banditComboExpConfig?.enabled) {
		return { psfComboBanditVariation: null, psfComboData: null };
	}

	const { cookies, i18n } = utilityContext;
	const psfComboExpPercentage = Math.floor(Math.random() * 100);
	const psfComboBanditVariation = psfComboBanditVariationWhenEnabled(
		cookies,
		banditComboExpConfig,
		psfComboExpPercentage,
	);

	if (psfComboBanditVariation === 'default') {
		return { psfComboBanditVariation: 'default', psfComboData: null };
	}

	const banditAmounts = banditComboExpConfig.bandit_amounts;
	const amount =
		banditAmounts?.[Math.floor(Math.random() * banditAmounts.length)] ||
		viewer.country.promotionCountrySettings?.anchorAmount ||
		0;
	const currency = viewer.country.currency?.code || 'USD';

	return {
		psfComboBanditVariation: 'static',
		psfComboData: {
			amount,
			currency,
			amountLocalized: i18n.localizeCurrency(amount, currency),
		},
	};
}

function isPromoteShareActionVariation(variation: PromoteShareOnComboExperiment['variation']): boolean {
	return variation === 'variant_1';
}

type Params = {
	petition: Petition;
	promotionStats: PromotionStats;
	viewer: Viewer;
	experiments: Experiments;
	recruiter?: Recruiter;
	featureConfigs: FcmValues;
};

export async function normalizeData(
	utilityContext: UtilityContext,
	{ petition, promotionStats, viewer, experiments, recruiter, featureConfigs }: Params,
): Promise<PsfPromoteOrSharePageData> {
	const { tagsConnection, ...rest } = petition;
	const normalizedCategoryTags = tagsConnection?.nodes.map((tag) => tag.name) ?? [];

	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);
	const promoteShareOnComboExperimentVariation = await normalizePromoteShareOnComboExperiment({
		experiment: experiments.promoteShareOnComboExperiment,
		utilityContext,
	});

	const hideMediaSlot = !getDisplayablePetitionPhoto(petition.photo);
	const experimentFlags = normalizeExperimentFlags(experiments);
	const { goal: promotionGoal, goalLocalized: promotionGoalLocalized } = normalizeGoal(
		utilityContext,
		promotionStats.promoterCount,
	);

	return {
		petition: { ...rest, promotionStats, categoryTags: normalizedCategoryTags },
		recruiter,
		joinTheCommunityCustomConfig: normalizeJoinTheCommunityCustomConfig(utilityContext, {
			viewer,
			fcmValues: featureConfigs,
		}),
		...normalizeAmount(utilityContext, featureConfigs, viewer, promoteShareOnComboExperimentVariation),
		promotionGoal,
		promotionGoalLocalized,
		...experimentFlags,
		promoteShareOnComboExperiment: {
			variation: promoteShareOnComboExperimentVariation,
		},
		psfShareFirstSocialStarterExperiment: PSF_SHARE_FIRST_SOCIAL_STARTER_COMBO_FALLBACK,
		hideMediaSlot,
		hideRecruiterEnabled: featureConfigs.hideRecruiterEnabled,
		isShareSlideUpEnabled: featureConfigs.isShareSlideUpEnabled,
		isShareAssetsEnabled: featureConfigs.isShareAssetsEnabled,
		channelAwareCtaChannels: featureConfigs.channelAwareCtaChannels,
		facebookPostSheetFcm: featureConfigs.facebookPostSheetFcm,
		campaignActionsEnabled: Boolean(featureConfigs.campaignActionsConfig?.enabled),
		useDonationLanguage: featureConfigs.useDonationLanguage,
		advocateEntryPointButtonLabel: featureConfigs.advocateEntryPointConfig.buttonLabel,
		advocateEntryPointCpDisabled: featureConfigs.advocateEntryPointConfig.disabledCps.includes(petition.slug),
		signaturesFromShareCountEstimateLocalized: utilityContext.i18n.localizeNumber(signaturesFromShareCountEstimate),
		promoteFormRawData: {
			viewer,
			featureConfigs,
		},
	};
}

function normalizeAmount(
	utilityContext: UtilityContext,
	fcmValues: PsfPromoteOrShareDataQuery['featureConfigs'],
	viewer: Viewer,
	promoteShareVariation: PromoteShareOnComboExperiment['variation'],
) {
	const { i18n } = utilityContext;
	const viewerCurrency = viewer.country.currency || { code: 'USD', exchangeRate: 1 };

	if (isPromoteShareActionVariation(promoteShareVariation)) {
		const chipInAmount = CHIP_IN_USD_SHARE_EXPERIMENT_AMOUNT;
		return {
			chipInAmount,
			chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, viewerCurrency.code),
			chipInCurrencyCode: viewerCurrency.code,
			psfComboBanditVariation: null,
		};
	}

	const { psfComboData, psfComboBanditVariation } = resolvePsfComboBandit(utilityContext, fcmValues, viewer);
	if (psfComboData?.amount) {
		return {
			chipInAmount: psfComboData.amount,
			chipInAmountLocalized: psfComboData.amountLocalized,
			chipInCurrencyCode: psfComboData.currency,
			psfComboBanditVariation,
		};
	}

	const amountChoices = viewer.country.promotionCountrySettings?.amountChoices;
	const smallestAmountChoice = amountChoices?.length ? Math.min(...amountChoices) : undefined;
	const chipInAmount = smallestAmountChoice || Math.round(CHIP_IN_USD_FALLBACK * viewerCurrency.exchangeRate);
	return {
		chipInAmount,
		chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, viewerCurrency.code),
		chipInCurrencyCode: viewerCurrency.code,
		psfComboBanditVariation,
	};
}

function normalizeGoal({ i18n }: UtilityContext, count: number) {
	const goal = calculateNextRoundGoal(count);

	return {
		goal,
		goalLocalized: i18n.localizeNumber(goal),
	};
}
